import React, { useEffect, useState } from "react";
import { IconArrowLeft } from "@velaro/velaro-component-lib/";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import { useLocation } from "react-router-dom";
import { IconPlus } from "@velaro/velaro-component-lib/";
const SecurityLogo = "/img/velaro_security-safe-encryption.png";
const IntegrationCompleteLogo = "/img/velaro_integrations-complete.png";
import AccelaPanel from "./AccelaPanel";
import { useNavigate } from "react-router-dom";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import { openAccelaAuthPopup } from "./AccelaAuthPopup";

interface Props {
  appStatus?: string;
}

interface AccelaConfigById {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret?: string;
  emailAddress: string;
  teamId: number;
  scope?: string;
  refreshTokenExpired?: boolean;
  token?: string;
  environemnt?: string;
}

interface SetupResult {
  ok: boolean;
  id?: number;
}

export default function AccelaCreate(props: Props) {
  const [touched, setTouched] = useState({
    clientId: false,
    clientSecret: false,
    agencyName: false,
    emailAddress: false,
    environment: false,
    scope: false
  });

  const isEmpty = (val: string) => !val || val.trim() === "";
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [environment, setEnvironment] = useState("");

  const [scope, setScope] = useState(
    "records addresses contacts inspections owners parcels documents workflows conditions professionals global_search offline_access"
  );
  const { teamId } = useSelectedTeam();
  const api = useApi();

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);

  const location = useLocation();
  const { appStatus } = location.state || {};
  const [configId, setConfigId] = useState<number>(-1);
  const [step, setStep] = useState(appStatus === "Pending" ? 3 : 1);
  const [panels, setPanels] = useState<{ id: number; scopeType: string }[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [config, setConfig] = useState<AccelaConfigById | null>(null);
  const navigate = useNavigate();
  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, scopeType: "" }]);

  useEffect(() => {
    if (step === 3 && panels.length === 0) {
      addPanel();
    }
  }, [step]);

  useEffect(() => {
    async function fetchAccelaConfig() {
      if (configId <= 0) return;

      try {
        const resp = await api.messaging.get(`Accela/Integrations/${configId}`);
        if (resp.ok) {
          const data: AccelaConfigById[] = await resp.json();
          if (data.length > 0) {
            setConfig(data[0]);
          } else {
            console.warn("No config data found for the given ID");
          }
        } else {
          console.error("Failed to fetch Accela config");
        }
      } catch (error) {
        console.error("Error fetching Accela config:", error);
      }
    }

    fetchAccelaConfig();
  }, [configId]);

  useEffect(() => {
    if (config && appStatus !== "Connected") {
      setEmailAddress(config.emailAddress || "");
      setClientId(config.clientId || "");
      setClientSecret(config.clientSecret || "");
      setAgencyName(config.agencyName || "");
      setEnvironment(config.environemnt || "");
    }
  }, [config, appStatus]);

  useEffect(() => {
    if (appStatus === "Pending") {
      setStep(3);
    }
  }, [appStatus]);

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  async function handleSubmit() {
    if (step > 1) {
      setStep((prev) => prev + 1);
      return;
    }

    setSending(true);
    await handleSetup();
  }

  async function handleSetup() {
    if (
      [
        clientId,
        clientSecret,
        agencyName,
        emailAddress,
        environment,
        scope
      ].some(isEmpty)
    ) {
      setTouched({
        clientId: true,
        clientSecret: true,
        agencyName: true,
        emailAddress: true,
        environment: true,
        scope: true
      });
      setSending(false);
      return;
    }

    const saveResp = await api.messaging.post("Accela/Auth/SaveCredentials", {
      teamId,
      clientId,
      clientSecret,
      agencyName,
      emailAddress,
      accelaBaseUrl: "https://apis.accela.com/v4",
      scope
    });

    if (!saveResp.ok) {
      console.error("Failed to save Accela credentials");
      setSending(false);
      return;
    }

    openAccelaAuthPopup({
      clientId,
      agencyName,
      environment,
      scope,
      onAuthComplete: async ({ code, state }) => {
        const exchangeResp = await api.messaging.post(
          "Accela/Auth/ExchangeCode",
          {
            code,
            state: state || "",
            clientId
          }
        );

        if (exchangeResp.ok) {
          const json = await exchangeResp.json();
          const result = { ok: true, id: json.id };

          setSuccess(result.ok);
          if (result.ok && result.id) {
            setConfigId(result.id);
            setStep((prev) => prev + 1);
          }
        } else {
          console.error("Token exchange failed");
        }

        setSending(false);
      },
      onError: (err) => {
        setSending(false);
      }
    });
  }

  const isNextActive =
    emailAddress && clientId && clientSecret && agencyName && scope;

  const nextBtnClass = !isNextActive
    ? "bg-slate-300 border-slate-300 hover:bg-slate-300 hover:border-slate-500 text-white cursor-not-allowed"
    : "";

  return (
    <div className="panel bg-white rounded-2xl shadow-md mb-4 p-6 xl:max-w-[1350px] xl:justify-center xl:mx-auto">
      <div className="w-full justify-between items-center inline-flex">
        <div className="w-full rounded-lg justify-between items-center gap-2 flex">
          <div className="text-center text-slate-500 text-sm font-medium flex">
            {step > 1 ? (
              <button
                className="btn flex items-center gap-2"
                onClick={() => setStep(step - 1)}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            ) : (
              <button
                className="btn flex items-center gap-2"
                onClick={() => navigate("/integrations")}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            )}
          </div>
          {step < 4 && (
            <PrimaryBlueButton
              label="Next"
              onClick={async () => {
                if (isNextActive) {
                  await handleSubmit();
                }
              }}
              className={nextBtnClass}
              disabled={!isNextActive || sending}
            />
          )}
        </div>
      </div>

      <div className="flex items-center w-full mt-4">
        {[
          "Authentication",
          "Connection Success",
          "Field Mapping",
          "Completed"
        ].map((label, index) => {
          const isActive = step >= index + 1;
          const isCurrent = step === index + 1;
          const isCompleted = step > index + 1;
          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 z-10 ${
                    isActive ? "border-primary" : "border-slate-300"
                  } bg-white`}
                >
                  <div
                    className={`w-6 h-6 rounded-full z-10 ${
                      isActive ? "bg-primary" : "bg-slate-300"
                    }`}
                  ></div>
                </div>
                <p
                  className={`mt-2 font-medium text-sm ${
                    isCurrent
                      ? "text-primary"
                      : isCompleted
                      ? "text-slate-800"
                      : "text-slate-400"
                  }`}
                >
                  {label}
                </p>
              </div>
              {index < 3 && (
                <div
                  className={`flex-1 border-t-4 mb-8 -mx-[51px] z-0 ${
                    step > index + 1
                      ? "border-primary "
                      : step === index + 1
                      ? "border-primary border-dashed"
                      : "border-slate-300 border-dashed"
                  }`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div className="w-1/3 place-self-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <div className="text-center text-slate-900 text-xl font-medium">
              Accela Configuration
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Agency Name
              </label>
              <input
                type="text"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, agencyName: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              {touched.agencyName && isEmpty(agencyName) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Email Address
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, emailAddress: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              {touched.emailAddress && isEmpty(emailAddress) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, clientId: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              {touched.clientId && isEmpty(clientId) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Client Secret
              </label>
              <input
                type="text"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, clientSecret: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              {touched.clientSecret && isEmpty(clientSecret) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Environment
              </label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, environment: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              >
                <option value="TEST">TEST</option>
                <option value="PROD">PROD</option>
              </select>
              {touched.environment && isEmpty(environment) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Scope
              </label>
              <textarea
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, scope: true }))}
                className="w-full px-4 py-2 border rounded-lg"
                rows={3}
                required
              />
              {touched.scope && isEmpty(scope) && (
                <span className="text-red-500 text-xs">Required</span>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="w-1/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img
              className="w-40 place-self-center"
              src={SecurityLogo}
              alt=""
            ></img>
            <div className="text-center text-slate-800 text-xl font-medium">
              Congratulations! Successful Integration Connection
            </div>
            <p className="text-center text-slate-800 text-sm font-medium">
              Enjoy a seamless flow of data between Velaro and the Accela.
              <br />
              Take the next step to tailor your integration by configuring
              settings.
            </p>

            <div className="flex gap-2 justify-center">
              <button
                type="submit"
                className="rounded-lg items-center px-6 py-2 bg-white rounded-lg border border-cornflower-primary hover:border-primary-hover text-center text-cornflower-primary text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Finish Later
              </button>
              <PrimaryBlueButton
                label="Set up Integration"
                onClick={() => {
                  setStep(step + 1);
                }}
                className=""
              />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="w-full py-8">
          <div className="w-full pb-8 flex gap-6">
            <div className="flex flex-col flex-1 gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Email Address
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </div>

            <div className="flex flex-col flex-1 gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Agency Name
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-slate-100 rounded-lg border border-slate-300"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                disabled
              />
            </div>

            <div className="flex flex-col flex-1 gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Accela Client ID
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-slate-100 rounded-lg border border-slate-300"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled
              />
            </div>
          </div>
          <hr className="py-4"></hr>
          <div className="flex justify-between">
            <div>
              <div className="text-slate-800 text-base font-medium">
                Choose which records to sync
              </div>
              <div className="text-slate-500 text-sm font-normal">
                Mappings connect individual fields with matching information
                from each app.{" "}
              </div>
            </div>
            <PrimaryBlueButton
              label="Add Mapping"
              onClick={addPanel}
              className=""
              prefixIcon={React.createElement(IconPlus)}
            />
          </div>

          <div className="mt-4">
            {panels.map((panel) => (
              <AccelaPanel
                key={panel.id}
                teamId={teamId!}
                scopeType={panel.scopeType}
                showSavedMessage={showSavedMessage}
                isCreateMode={true}
                selectedConfigId={configId}
                isDeleteDisabled={panels.length === 1}
              />
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="w-1/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img
              className="w-40 place-self-center"
              src={IntegrationCompleteLogo}
              alt=""
            ></img>
            <div className="text-center text-slate-800 text-xl font-medium">
              Completed Integration!
            </div>
            <p className="text-center text-slate-800 text-sm font-normal">
              Your integration is successfully set up and ready to use. <br />
              All connected features are now active and syncing as expected.
            </p>

            <div className="flex gap-2 justify-center">
              <button
                type="button"
                className="rounded-lg items-center px-6 py-2 bg-white rounded-lg border border-cornflower-primary hover:border-primary-hover text-center text-cornflower-primary text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Go to Dashboard
              </button>
              <PrimaryBlueButton
                label="View Configuration"
                onClick={() =>
                  (window.location.href = "/integrations/accela-civic")
                }
                className=""
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
